import React from "react";
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../../../../catalyst-components/button";
import { Badge } from "../../../../catalyst-components/badge";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "../../../../catalyst-components/table";
import { Spinner } from "../../../../catalyst-components/spinner";
import { Id } from "../../../../convex/_generated/dataModel";
import { MainRoutes } from "../../../../shared/models";

const SectionHeader = ({ title, icon }: { title: string; icon: string }) => (
  <div className="flex items-center gap-2 mb-4">
    <span className="text-2xl">{icon}</span>
    <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
  </div>
);

const InfoCard = ({ 
  title, 
  children, 
  className = "" 
}: { 
  title: string; 
  children: React.ReactNode; 
  className?: string 
}) => (
  <div className={`bg-primary-1 rounded-lg shadow-sm border border-primary-4 p-4 sm:p-6 ${className}`}>
    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2 mb-4">{title}</h3>
    {children}
  </div>
);

const EmptyState = ({ message }: { message: string }) => (
  <div className="text-center py-8 text-gray-500 italic">
    {message}
  </div>
);

export const SessionDetail: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const sessionDetails = useQuery(
    api.session_details.getSessionDetails,
    sessionId ? { session_id: sessionId as Id<"sessions"> } : "skip"
  );

  const insights = useQuery(
    api.session_details.getSessionInsights,
    sessionId ? { session_id: sessionId as Id<"sessions"> } : "skip"
  );

  const actionItems = useQuery(
    api.session_details.getSessionActionItems,
    sessionId ? { session_id: sessionId as Id<"sessions"> } : "skip"
  );

  const notesData = useQuery(
    api.session_details.getSessionNotes,
    sessionId ? { session_id: sessionId as Id<"sessions"> } : "skip"
  );

  if (!sessionId) {
    return (
      <section className="min-h-screen bg-background pt-24 px-6 flex flex-col justify-start px-4">
        <div className="flex items-center mb-6">
          <Button
            outline
            color="zinc"
            onClick={() => navigate(-1)}
            className="mr-4"
          >
            ← Back
          </Button>
          <h1 className="text-3xl font-bold">Session Detail</h1>
        </div>
        <div className="text-center py-8 text-red-500">
          No session ID provided
        </div>
      </section>
    );
  }

  if (!sessionDetails || !insights || !actionItems || !notesData) {
    return (
      <section className="min-h-screen bg-background pt-24 px-6 flex flex-col justify-start px-4">
        <div className="flex items-center mb-6">
          <Button
            outline
            color="zinc"
            onClick={() => navigate(-1)}
            className="mr-4"
          >
            ← Back
          </Button>
          <h1 className="text-3xl font-bold">Session Detail</h1>
        </div>
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </section>
    );
  }

  const formatDate = (timestamp?: string | number) => {
    if (!timestamp) return "N/A";
    const date = typeof timestamp === "number" ? new Date(timestamp) : new Date(timestamp);
    return date.toLocaleString();
  };

  const formatCategory = (category: string) => {
    const categoryMap: Record<string, { label: string; color: "amber" | "green" | "blue" | "purple" }> = {
      breakthrough: { label: "Breakthrough", color: "amber" },
      realization: { label: "Realization", color: "green" },
      question: { label: "Question", color: "blue" },
      action_item: { label: "Action Item", color: "purple" },
    };
    return categoryMap[category] || { label: category, color: "amber" };
  };

  const formatPriority = (priority: string) => {
    const priorityMap: Record<string, { label: string; color: "red" | "amber" | "green" }> = {
      high: { label: "High", color: "red" },
      medium: { label: "Medium", color: "amber" },
      low: { label: "Low", color: "green" },
    };
    return priorityMap[priority] || { label: priority, color: "amber" };
  };

  return (
    <section className="min-h-screen bg-background pt-24 px-6 flex flex-col justify-start px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Button
            outline
            color="zinc"
            onClick={() => navigate(-1)}
            className="mr-4"
          >
            ← Back
          </Button>
          <h1 className="text-3xl font-bold">Session Detail</h1>
        </div>
        <Button
          color="light"
          onClick={() => navigate(`${MainRoutes.Chat}?sessionId=${sessionId}&readonly=true`)}
        >
          View Chat
        </Button>
      </div>

      {/* Session Overview */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoCard title="Session Info" className="md:col-span-2">
          <div className="space-y-3">
            <div>
              <span className="font-semibold text-gray-600">Assistant: </span>
              <span className="text-gray-900">
                {sessionDetails.assistant?.display_name || sessionDetails.assistant?.name || "Unknown"}
              </span>
            </div>
            <div>
              <span className="font-semibold text-gray-600">Skill: </span>
              <span className="text-gray-900">{sessionDetails.skill?.name || "Unknown"}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-600">User: </span>
              <span className="text-gray-900">
                {sessionDetails.user?.firstName} {sessionDetails.user?.lastName} ({sessionDetails.user?.email})
              </span>
            </div>
            <div>
              <span className="font-semibold text-gray-600">Started: </span>
              <span className="text-gray-900">{formatDate(sessionDetails.session._creationTime)}</span>
            </div>
            {sessionDetails.session.isComplete && (
              <div>
                <span className="font-semibold text-gray-600">Completed: </span>
                <span className="text-gray-900">{formatDate(sessionDetails.session.completed_timestamp)}</span>
              </div>
            )}
            <div>
              <span className="font-semibold text-gray-600">Status: </span>
              <Badge color={sessionDetails.session.isComplete ? "green" : "amber"}>
                {sessionDetails.session.isComplete ? "Completed" : "In Progress"}
              </Badge>
            </div>
          </div>
        </InfoCard>

        <InfoCard title="Summary">
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{sessionDetails.counts.insights}</div>
              <div className="text-sm text-gray-600">Insights</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">{sessionDetails.counts.actionItems}</div>
              <div className="text-sm text-gray-600">Action Items</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {notesData.notes ? "✓" : "—"}
              </div>
              <div className="text-sm text-gray-600">Notes</div>
            </div>
          </div>
        </InfoCard>
      </div>

      {/* Insights Section */}
      <div className="mb-6">
        <SectionHeader title="Insights" icon="💡" />
        <InfoCard title={`All Insights (${insights.length})`}>
          {insights.length === 0 ? (
            <EmptyState message="No insights captured during this session." />
          ) : (
            <Table striped className="w-full">
              <TableHead className="border-b-2 border-primary-2">
                <TableRow>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Insight</TableHeader>
                  <TableHeader>Captured</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {insights.map((insight) => {
                  const { label, color } = formatCategory(insight.category);
                  return (
                    <TableRow key={insight._id} className="hover:bg-gray-50">
                      <TableCell>
                        <Badge color={color}>{label}</Badge>
                      </TableCell>
                      <TableCell className="font-medium max-w-md">
                        {insight.insight}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatDate(insight.timestamp)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </InfoCard>
      </div>

      {/* Action Items Section */}
      <div className="mb-6">
        <SectionHeader title="Action Items" icon="✅" />
        <InfoCard title={`All Action Items (${actionItems.length})`}>
          {actionItems.length === 0 ? (
            <EmptyState message="No action items created during this session." />
          ) : (
            <Table striped className="w-full">
              <TableHead className="border-b-2 border-primary-2">
                <TableRow>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Priority</TableHeader>
                  <TableHeader>Action</TableHeader>
                  <TableHeader>Due Date</TableHeader>
                  <TableHeader>Created</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {actionItems.map((item) => {
                  const { label, color } = formatPriority(item.priority);
                  return (
                    <TableRow key={item._id} className="hover:bg-gray-50">
                      <TableCell>
                        <Badge color={item.completed ? "green" : "amber"}>
                          {item.completed ? "Done" : "Pending"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge color={color}>{label}</Badge>
                      </TableCell>
                      <TableCell className="font-medium max-w-md">
                        {item.action}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {item.dueDate || "—"}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatDate(item.createdAt)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </InfoCard>
      </div>

      {/* Notes Section */}
      <div className="mb-6">
        <SectionHeader title="Session Notes" icon="📝" />
        <InfoCard title="Notes">
          {!notesData.notes || notesData.notes.trim() === "" ? (
            <EmptyState message="No notes were saved during this session." />
          ) : (
            <div className="prose prose-sm max-w-none">
              <pre className="whitespace-pre-wrap text-gray-800 font-sans text-sm leading-relaxed bg-gray-50 p-4 rounded">
                {notesData.notes}
              </pre>
            </div>
          )}
        </InfoCard>
      </div>
    </section>
  );
};
